import { Component, ElementRef, EventEmitter, HostBinding, Input, Output, ViewChild } from '@angular/core';
import { AgMediaUploadModel, DEFAULT_MAX_MEDIA_UPLOAD_SIZE_MB } from '../ag-media-uploader-modal.models';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { extensionToMimeMap, fileToUIBase64 } from '../ag-media-uploader-modal.utils';
import { getFileExtension } from '../../../utils/file.utils';
import { AgMediaUploaderModalService } from '../ag-media-uploader-modal.service';
import {
  downscaleImageToFit,
  loadImageFromBlob,
} from 'ag-common-svc/lib/components/ag-media-uploader/utils/downscale-image-to-fit.util';

@Component({
  selector: 'ag-shr-upload-from-disk-tab',
  templateUrl: './upload-from-disk-tab.component.html',
  styleUrls: ['./upload-from-disk-tab.component.scss'],
})
export class UploadFromDiskTabComponent {
  @HostBinding('class') className = 'upload-from-disk-tab';

  @ViewChild('fileInput', { static: false }) fileInputRef: ElementRef<HTMLInputElement>;

  @Input() accept = ['image/*'];
  @Input() placeholder = 'Click or Drag an Image';
  @Input() size: 'small' | 'medium' | 'large' | 'unspecified' = 'unspecified';
  @Input() maxFileSizeMb = DEFAULT_MAX_MEDIA_UPLOAD_SIZE_MB;
  @Input() applyImageDownscale = false;
  @Input()
  set isSplitLayout(value: boolean) {
    this.className = value ? 'upload-from-disk-tab upload-from-disk-tab--split' : 'upload-from-disk-tab';
  }
  @Output() mediaUrlChange = new EventEmitter<AgMediaUploadModel | null>();

  protected imagePreviewUrl: string;
  protected isImageSelected = false;
  protected qualityPercent = 80;
  protected readonly minQualityPercent = 50;
  protected readonly maxQualityPercent = 95;
  protected readonly qualityStep = 5;
  protected optimizedSizeBytes: number | null = null;
  protected originalSizeBytes: number | null = null;
  protected isOptimizing = false;

  private _file: File;
  private _fileBase64: string;
  private _originalFile: File;
  private _optimizeToken = 0;
  private _isFileValid$ = new BehaviorSubject(true);

  constructor(
    private toastrService: ToastrService,
    private agMediaUploaderService: AgMediaUploaderModalService,
  ) {}

  handleIsFileValid = (isValid): void => {
    this._isFileValid$.next(isValid);

    if (isValid) {
      this.syncSelectedFile();
      return;
    }

    this.agMediaUploaderService.setFile(null);
  };

  clearFile() {
    this.imagePreviewUrl = null;
    this._file = null;
    this._fileBase64 = null;
    this._originalFile = null;
    this.optimizedSizeBytes = null;
    this.originalSizeBytes = null;
    this.isImageSelected = false;
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
    this._isFileValid$.next(true);
  }

  // Method triggered on file input change
  async fileChangeEvent(event: Event) {
    this.imagePreviewUrl = null;
    this._fileBase64 = null;
    this.optimizedSizeBytes = null;
    this.originalSizeBytes = null;
    const file: File = (event?.target as HTMLInputElement)?.files[0];

    if (!file) {
      return;
    }

    const isImage = file.type?.startsWith('image/');
    // When downscale is enabled, size is validated after compression.
    const isFileValid =
      isImage && this.applyImageDownscale ? this.isFileTypeAllowed(file) : this.validateFile(file);
    if (!isFileValid) {
      return;
    }

    this._file = file;
    this._originalFile = file;
    this.originalSizeBytes = file.size;
    this.isImageSelected = isImage;

    if (this.isImageSelected) {
      await this.applyOptimization();
      return;
    }

    this._fileBase64 = await fileToUIBase64(file).catch(() => {
      this._isFileValid$.next(false);
      return null;
    });

    if (!this._fileBase64) {
      return;
    }

    this.setPreview();
    this.syncSelectedFile();
  }

  clear() {
    this.imagePreviewUrl = null;
    this._file = null;
  }

  openFileDialog() {
    this.fileInputRef?.nativeElement?.click();
  }

  adjustQuality(delta: number) {
    const next = Math.max(
      this.minQualityPercent,
      Math.min(this.maxQualityPercent, this.qualityPercent + delta),
    );
    if (next === this.qualityPercent) {
      return;
    }
    this.qualityPercent = next;
    void this.applyOptimization();
  }

  private setPreview = () => {
    const isEmailFile = this.isEmailFile(this._file);

    if (isEmailFile) {
      this.imagePreviewUrl = '/assets/file-placeholders/email-opened.png';
      return;
    }

    if (this._file?.type?.startsWith('image/')) {
      this.imagePreviewUrl = this._fileBase64;
      return;
    }

    this.imagePreviewUrl = '/assets/file-placeholders/email-opened.png';
  };

  private async applyOptimization(): Promise<void> {
    if (!this._originalFile || !this.isImageSelected) {
      return;
    }

    const token = ++this._optimizeToken;
    this.isOptimizing = true;

    try {
      if (this.applyImageDownscale) {
        await this.applySizeAwareDownscale(token);
        return;
      }

      await this.applyDefaultOptimization(token);
    } catch (error) {
      this._file = this._originalFile;
      this.optimizedSizeBytes = null;
      this._fileBase64 = await fileToUIBase64(this._originalFile).catch(() => null);
      if (this._fileBase64 && (!this.applyImageDownscale || this.validateSize(this._originalFile.size))) {
        this.imagePreviewUrl = this._fileBase64;
        this.syncSelectedFile();
        return;
      }

      this._isFileValid$.next(false);
      this.agMediaUploaderService.setFile(null);
    } finally {
      if (token === this._optimizeToken) {
        this.isOptimizing = false;
      }
    }
  }
  /*
  * Difference between applyDefaultOptimization and applySizeAwareDownscale
  * default = “make it a bit nicer/smaller”; size-aware = “keep reducing until it fits the quota.”
  * Max side: default = Fixed 2000px; size-aware = Depends on maxFileSizeMb (2000 → down to 320)
  * Format: default = Preserve original; size-aware = Force JPEG
  * May fail on size?: default = No (already validated earlier); size-aware = Yes, if still too big
  * */
  private async applySizeAwareDownscale(token: number): Promise<void> {
    const image = await loadImageFromBlob(this._originalFile);
    if (token !== this._optimizeToken) {
      return;
    }

    const maxBytes = this.maxFileSizeMb * 1024 * 1024;
    const result = await downscaleImageToFit(image, {
      maxBytes,
      qualityPercent: this.qualityPercent,
      minQualityPercent: this.minQualityPercent,
      qualityStep: this.qualityStep,
      forceJpeg: true,
      sourceType: this._originalFile.type,
      fileName: this._originalFile.name,
    });

    if (token !== this._optimizeToken) {
      return;
    }

    if (result.file.size > maxBytes) {
      this.toastrService.error(
        `Unable to downscale image under ${this.getMaxFileSizeLabel()}. Try a smaller image.`,
      );
      this.clearFile();
      this.agMediaUploaderService.setFile(null);
      return;
    }

    this.qualityPercent = result.qualityPercent;
    this._file = result.file;
    this.optimizedSizeBytes = result.file.size;

    this._fileBase64 = await fileToUIBase64(result.file).catch(() => null);
    if (!this._fileBase64) {
      return;
    }

    this.imagePreviewUrl = this._fileBase64;
    this.syncSelectedFile();
  }

  private async applyDefaultOptimization(token: number): Promise<void> {
    const image = await this.loadImage(this._originalFile);
    if (token !== this._optimizeToken) {
      return;
    }

      const maxSide = 2000;
      const ratio = Math.min(1, maxSide / Math.max(image.width, image.height));
      const targetWidth = Math.max(1, Math.round(image.width * ratio));
      const targetHeight = Math.max(1, Math.round(image.height * ratio));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas not supported');
      }

      ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

      const quality = this.qualityPercent / 100;
      const outputType = this._originalFile.type === 'image/png' ? 'image/png' : 'image/jpeg';

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          value => {
            if (!value) {
              reject(new Error('Failed to encode image'));
              return;
            }
            resolve(value);
          },
          outputType,
          outputType === 'image/jpeg' ? quality : undefined,
        );
      });

      if (token !== this._optimizeToken) {
        return;
      }

      const optimizedFile = new File([blob], this._originalFile.name, { type: outputType });
      this._file = optimizedFile;
      this.optimizedSizeBytes = optimizedFile.size;

      this._fileBase64 = await fileToUIBase64(optimizedFile).catch(() => null);
      if (!this._fileBase64) {
        return;
      }

    this.imagePreviewUrl = this._fileBase64;
    this.syncSelectedFile();
  }

  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = error => {
        URL.revokeObjectURL(url);
        reject(error);
      };
      img.src = url;
    });
  }

  protected formatBytes(bytes?: number | null): string {
    if (!bytes) {
      return '';
    }
    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }
    return `${(kb / 1024).toFixed(1)} MB`;
  }

  private isEmailFile(file: File): boolean {
    const isEmailType = file.type === 'message/rfc822' || file.type === 'application/vnd.ms-outlook';
    const isEmailExtension = file.name.endsWith('.eml') || file.name.endsWith('.msg');

    return isEmailType || isEmailExtension;
  }

  private validateFile(file: File): boolean {
    return this.validateSize(file.size) && this.isFileTypeAllowed(file);
  }

  private isFileTypeAllowed(file: File): boolean {
    const { type, name } = file;

    if (!this.accept?.length) {
      return true;
    }

    const isMimeTypeValid = this.accept.some(acceptedType => this.mimeTypeMatches(type, acceptedType));

    const extension = getFileExtension(name);
    const isExtensionValid = this.accept.some(acceptedType => this.extensionMatchesMimeType(acceptedType, extension));

    // Additional validation for .msg (Outlook) files
    if (extension === 'msg') {
      return this.validateOutlookFile(type);
    }

    if (!isMimeTypeValid && !isExtensionValid) {
      this.toastrService.error('Invalid file type! Only specific types are allowed.');
      return false;
    }

    return true;
  }

  private extensionMatchesMimeType(acceptedType: string, extension: string): boolean {
    return extensionToMimeMap[extension] === acceptedType;
  }

  private mimeTypeMatches(type: string, acceptedType: string): boolean {
    if (!acceptedType) {
      return false;
    }

    if (acceptedType === '*/*') {
      return true;
    }

    if (!type) {
      return false;
    }

    if (acceptedType.endsWith('/*')) {
      const acceptedRoot = acceptedType.split('/')[0];
      return type.startsWith(`${acceptedRoot}/`);
    }

    return type === acceptedType;
  }

  private syncSelectedFile(): void {
    if (!this._file || !this._fileBase64) {
      return;
    }

    this.agMediaUploaderService.setFile({
      contentType: this._file.type,
      fileName: this._file.name,
      base64: this._fileBase64,
      file: this._file,
    });
  }

  // Further validation for specific file types
  private validateOutlookFile(type: string): boolean {
    return type === 'application/vnd.ms-outlook' || type === '';
  }

  private validateSize(fileSize: number): boolean {
    const maxAllowedSize = this.maxFileSizeMb * 1024 * 1024;
    if (fileSize > maxAllowedSize) {
      this.toastrService.error(
        `File is too big! Max allowed size is ${this.getMaxFileSizeLabel()}. Image 1920 px width or height is recommended.`,
      );
      return false;
    }
    return true;
  }

  private getMaxFileSizeLabel(): string {
    if (this.maxFileSizeMb < 1) {
      return `${Math.round(this.maxFileSizeMb * 1024)}KB`;
    }

    return `${this.maxFileSizeMb}MB`;
  }
}
